import { useCallback, useEffect, useState } from "react";
import {
  fetchQuarantineById,
  updateQuarantine,
  promoteQuarantine,
  discardQuarantine,
} from "../../../services/aro/quarantineService";
import { fetchCertificateTypes } from "../../../services/aro/certificateService";
import { fetchEvents } from "../../../services/aro/eventService";
import { AROEvent, CertificateType, QuarantineDetail } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import Toast          from "../../shared/Toast/Toast";
import { formatDateLong, formatDateTime, formatLabel, formatLevel } from "../../../utils/formatters";
import { useAuth } from "../../../context/useAuth";
import "./QuarantineDetailPage.css";

interface Props {
  quarantineId: string;
  onBack: () => void;
}

// Corrections use the import's field names (see the backend's import_service.apply_corrections)
const CORRECTABLE_FIELDS: { key: string; label: string }[] = [
  { key: "email",          label: "Email" },
  { key: "credential_id",  label: "Credential ID" },
  { key: "full_name",      label: "Name" },
  { key: "earned_date",    label: "Issued on" },
  { key: "linkedin_url",   label: "LinkedIn" },
  { key: "company",        label: "Organization" },
  { key: "credential_url", label: "Credential URL" },
];

function statusVariant(status: QuarantineDetail["status"]) {
  if (status === "promoted")  return "green";
  if (status === "discarded") return "gray";
  return "amber";
}

export default function QuarantineDetailPage({ quarantineId, onBack }: Props) {
  const { email: changedBy } = useAuth();
  const [row,     setRow]     = useState<QuarantineDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [toast,   setToast]   = useState<{ msg: string; variant: "success" | "info" | "error" } | null>(null);

  const [certificateTypes, setCertificateTypes] = useState<CertificateType[]>([]);
  const [events,           setEvents]           = useState<AROEvent[]>([]);

  const [corrections,   setCorrections]   = useState<Record<string, string>>({});
  const [certTypeId,    setCertTypeId]    = useState("");
  const [eventId,       setEventId]       = useState("");
  const [saving,        setSaving]        = useState(false);
  const [promoting,     setPromoting]     = useState(false);
  const [discarding,    setDiscarding]    = useState(false);
  const [actionError,   setActionError]   = useState<string | null>(null);

  const applyRow = (detail: QuarantineDetail) => {
    setRow(detail);
    setCorrections(detail.corrected_fields ?? {});
    setCertTypeId(detail.certificate_type_id != null ? String(detail.certificate_type_id) : "");
    setEventId(detail.event_id != null ? String(detail.event_id) : "");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyRow(await fetchQuarantineById(quarantineId));
    } catch (e: any) {
      setError(e.message ?? "Failed to load quarantine row.");
    } finally {
      setLoading(false);
    }
  }, [quarantineId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchCertificateTypes().then((r) => setCertificateTypes(r.certificate_types)).catch(() => {});
    fetchEvents().then((r) => setEvents(r.events)).catch(() => {});
  }, []);

  const isEditable = row?.status === "quarantined";

  const handleCorrectionChange = (key: string, value: string) => {
    setCorrections((c) => ({ ...c, [key]: value }));
  };

  const saveCorrections = async () => {
    if (!row) return;
    const updated = await updateQuarantine(row.id, {
      corrected_fields:    Object.fromEntries(Object.entries(corrections).filter(([, v]) => v.trim())),
      certificate_type_id: certTypeId ? Number(certTypeId) : null,
      event_id:            eventId ? Number(eventId) : null,
    }, changedBy);
    applyRow(updated);
  };

  const handleSaveCorrections = async () => {
    setSaving(true);
    setActionError(null);
    try {
      await saveCorrections();
      setToast({ msg: "Corrections saved.", variant: "success" });
    } catch (e: any) {
      setActionError(e.message ?? "Failed to save corrections.");
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async () => {
    if (!row) return;
    setPromoting(true);
    setActionError(null);
    try {
      await saveCorrections();
      const result = await promoteQuarantine(row.id, changedBy);
      if (result.status === "promoted") {
        setToast({ msg: "Row promoted successfully.", variant: "success" });
      } else if (result.status === "discarded") {
        setToast({ msg: `Closed: ${result.reason_detail ?? "already imported"}`, variant: "info" });
      } else {
        setToast({ msg: `Still quarantined: ${formatLabel(result.reason)}${result.reason_detail ? ` — ${result.reason_detail}` : ""}`, variant: "error" });
      }
      await load();
    } catch (e: any) {
      setActionError(e.message ?? "Failed to promote row.");
    } finally {
      setPromoting(false);
    }
  };

  const handleDiscard = async () => {
    if (!row) return;
    setDiscarding(true);
    setActionError(null);
    try {
      await discardQuarantine(row.id, changedBy);
      setToast({ msg: "Row discarded.", variant: "info" });
      await load();
    } catch (e: any) {
      setActionError(e.message ?? "Failed to discard row.");
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <div className="aro-page">
      <Toast message={toast?.msg ?? null} variant={toast?.variant} onClose={() => setToast(null)} />

      <button className="aro-back-btn" onClick={onBack}>Back to Quarantine</button>

      {error && <div className="aro-error-banner">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : row ? (
        <>
          <PageHeader
            title={`Quarantine Row #${row.row_number}`}
            subtitle={`File ${row.file_id}`}
            right={<AROBadge label={formatLabel(row.status)} variant={statusVariant(row.status)} />}
          />

          <div className="aro-card">
            <h2 className="aro-section-title">Details</h2>
            <div className="quarantine-detail-grid">
              <div className="quarantine-detail-field">
                <span className="quarantine-detail-field__label">Reason</span>
                <span>{formatLabel(row.reason)}</span>
              </div>
              {row.reason_detail && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Reason Detail</span>
                  <span>{row.reason_detail}</span>
                </div>
              )}
              <div className="quarantine-detail-field">
                <span className="quarantine-detail-field__label">Created</span>
                <span>{formatDateTime(row.created_at)}</span>
              </div>
              {row.resolved_by && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Resolved By</span>
                  <span>{row.resolved_by}</span>
                </div>
              )}
              {row.resolved_at && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Resolved At</span>
                  <span>{formatDateTime(row.resolved_at)}</span>
                </div>
              )}
              {row.promoted_person_id != null && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Promoted Person ID</span>
                  <span>{row.promoted_person_id}</span>
                </div>
              )}
              {row.promoted_certificate_id != null && (
                <div className="quarantine-detail-field">
                  <span className="quarantine-detail-field__label">Promoted Certificate ID</span>
                  <span>{row.promoted_certificate_id}</span>
                </div>
              )}
            </div>
          </div>

          <div className="aro-card">
            <h2 className="aro-section-title">Original Row Data</h2>
            <table className="aro-table">
              <tbody>
                {Object.entries(row.raw_row).map(([key, value]) => (
                  <tr key={key}>
                    <td className="aro-table__cell--primary quarantine-raw-row__key">{key}</td>
                    <td>{value || <span className="aro-muted">(empty)</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="aro-card">
            <h2 className="aro-section-title">Corrections</h2>
            {!isEditable && (
              <p className="aro-muted">
                This row is {row.status} — corrections can only be made while a row is quarantined.
              </p>
            )}
            <div className="quarantine-corrections-grid">
              <div className="quarantine-edit-row">
                <label className="quarantine-edit-label">Certificate</label>
                <select
                  className="aro-select"
                  disabled={!isEditable}
                  value={certTypeId}
                  onChange={(e) => setCertTypeId(e.target.value)}
                >
                  <option value="">Choose a certificate…</option>
                  {certificateTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.issuer_company_name} — {t.certificate_name} ({formatLevel(t.level)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="quarantine-edit-row">
                <label className="quarantine-edit-label">Event</label>
                <select
                  className="aro-select"
                  disabled={!isEditable}
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                >
                  <option value="">No event</option>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.event_name} ({formatDateLong(ev.start_date)})</option>
                  ))}
                </select>
              </div>
              {CORRECTABLE_FIELDS.map(({ key, label }) => (
                <div key={key} className="quarantine-edit-row">
                  <label className="quarantine-edit-label">{label}</label>
                  <input
                    className="aro-input"
                    type={key === "earned_date" ? "date" : "text"}
                    disabled={!isEditable}
                    value={corrections[key] ?? ""}
                    placeholder="Enter corrected value…"
                    onChange={(e) => handleCorrectionChange(key, e.target.value)}
                  />
                </div>
              ))}
            </div>

            {actionError && <div className="aro-error-banner">{actionError}</div>}

            <div className="quarantine-detail-actions">
              {isEditable && (
                <button className="aro-btn aro-btn--ghost" onClick={handleSaveCorrections} disabled={saving}>
                  {saving ? "Saving…" : "Save Corrections"}
                </button>
              )}
              {isEditable && (
                <button className="aro-btn aro-btn--ghost" onClick={handleDiscard} disabled={discarding}>
                  {discarding ? "Discarding…" : "Discard"}
                </button>
              )}
              {isEditable && (
                <button className="aro-btn aro-btn--primary" onClick={handlePromote} disabled={promoting}>
                  {promoting ? "Promoting…" : "Promote"}
                </button>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
